// External libraries
import React, { useState } from 'react';
// @ts-expect-error untyped import
import { Tooltip } from '@checkout/react-components';
import {
  FloatingLabelSelect,
  // @ts-expect-error untyped import
} from '@checkout/react-components';
import { Alert } from '@paypalcorp/pp-react-alert-weasley-v7';

// Internal modules
import { PaypalThemedProvider } from '@/styles';
import { useStoreState } from '@/store';
import { ARIA_ATTRIBUTES } from '../shared';
import { containerStyles, tooltipErrorStyle } from './styles';
import { getCountryChangeHandler } from './handlers';
import {
  features,
  getContent,
  getLocale,
  logger,
  useMountLogging,
} from '@/lib';
import type { CountryCodes } from '@/generated/graphql';
import { isValidCountrySelection } from '../shared/helpers';
import { useShippingAddressCallbacks } from '@/hooks/shippingCallbacks';
import { LoadingElement } from '@/components/Common';

type BlockNonDomesticPayerProps = {
  hasBlockNonDomesticPayerRestriction: boolean;
};

const BlockNonDomesticPayer: React.FC<BlockNonDomesticPayerProps> = ({
  hasBlockNonDomesticPayerRestriction,
}) => {
  if (!hasBlockNonDomesticPayerRestriction) {
    return null;
  }

  return (
    <PaypalThemedProvider>
      <Alert id="country-info-message" type="info">
        {getContent('messages.blockNonDomesticPayer')}
      </Alert>
    </PaypalThemedProvider>
  );
};

const CountryDropdown = (): JSX.Element => {
  useMountLogging('CountryDropdown');
  const { country: globalsCountry, locale } = getLocale();
  const hasBlockNonDomesticPayerRestriction = features.isActive(
    'blockNonDomesticPayer',
  );
  const isBoltActive = features.isActive('blockDomesticPayers');
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const countriesList = useStoreState(({ api }) => {
    return api.griffinMetadata.territories[locale];
  });

  const isBlockedDomesticTransaction = useStoreState(
    ({ ui }) => ui.isBlockedDomesticTransaction,
  );
  const onShippingAddressCallback = useShippingAddressCallbacks();

  // We need to use local state for this component since we debounce the change
  // for non-Mac OS machines on the web. This is done so keyboard users don't
  // trigger a change every time they press the down/up arrow.
  const [country, setCountry] = useState(globalsCountry);

  if (!countriesList?.length) {
    return <LoadingElement />;
  }

  const isDisabled = isFormSubmitting || hasBlockNonDomesticPayerRestriction;
  const handleCountryChange = getCountryChangeHandler(
    isDisabled,
    onShippingAddressCallback,
  );

  return (
    <>
      <BlockNonDomesticPayer
        hasBlockNonDomesticPayerRestriction={
          hasBlockNonDomesticPayerRestriction
        }
      />

      <FloatingLabelSelect
        changeHandler={(
          e: React.ChangeEvent<HTMLSelectElement>,
          toCountry: CountryCodes,
        ) => {
          /**
           * @see https://paypal.atlassian.net/browse/LI-4008
           * Auto-fill features, such as Chrome, appear to be selecting
           * out-of-range values from the select causing an unexpected crash.
           *
           * In the event of an out-of-range selection the toCountry will be
           * and empty string and will fail validation. We log the values for
           * debugging and return to prevent the handlers from being called
           * which would otherwise crash.
           */
          if (!isValidCountrySelection('value', toCountry, countriesList)) {
            logger.info('weasley_dropdown_country_invalid_value', {
              selectedIndex: e?.target?.selectedIndex,
              toCountry,
              value: e?.target?.value,
            });
            return;
          }

          setCountry(toCountry);
          handleCountryChange(toCountry, e);
        }}
        data-testid="countrySelector"
        disabled={isDisabled}
        floatingLabelContainerStyleOverrides={
          hasBlockNonDomesticPayerRestriction && containerStyles
        }
        id="country"
        isChangeBlocked={hasBlockNonDomesticPayerRestriction}
        isRequired={true}
        label={getContent('fieldLabels.country')}
        name="country"
        selectOptions={countriesList}
        value={country}
        {...(hasBlockNonDomesticPayerRestriction && ARIA_ATTRIBUTES)}
      />

      {isBlockedDomesticTransaction && (
        <div data-testid="blocked-domestic-transaction-message">
          <Tooltip styleOverrides={tooltipErrorStyle}>
            {getContent('messages.blockedDomesticTransaction', {
              merchantCountry: getContent(`countryName.${country}`),
            })}
          </Tooltip>
        </div>
      )}

      {isBoltActive && (
        <Tooltip styleOverrides={tooltipErrorStyle}>
          {getContent('messages.blockOffDomesticTransactions')}
        </Tooltip>
      )}
    </>
  );
};

export { CountryDropdown };
